// src/pages/HdfcCardPage.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getHdfcCardRecords,
  deleteHdfcCardRecord,
  bulkDeleteHdfcCardRecords,
  importHdfcCardRecords,
  exportHdfcCardRecords,
  updateHdfcCardRecord,
  syncHdfcCardTaskStatus,
  getFieldExecutives,
} from "../actions/HdfcCardAction";
import Button from "../components/Button";
import Loading from "../components/Loading";
import Pagination from "../components/Pagination";
import Table from "../components/Table";
import DriveFilters, {
  driveFilterSelectClass,
} from "../components/DriveFilters";
import {
  Edit,
  Trash2,
  Upload,
  Download,
  Check,
  X,
  Trash,
  Eye,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const HdfcCardPage = () => {
  const dispatch = useDispatch();
  const { getHdfcCardRecordsData, loadingGetHdfcCardRecords } = useSelector(
    (state) => state.getHdfcCardRecords || {},
  );
  const [data, setData] = useState(getHdfcCardRecordsData?.data || []);
  const total = getHdfcCardRecordsData?.total || 0;
  const loading = loadingGetHdfcCardRecords || false;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [file, setFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const inputRef = useRef(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");
  const tableRef = useRef(null);
  const [editRemarkValue, setEditRemarkValue] = useState("");
  const [remarkUpdateLoading, setRemarkUpdateLoading] = useState(false);
  const [tatEditModalOpen, setTatEditModalOpen] = useState(false);
  const [tatEditValue, setTatEditValue] = useState("");
  const [tatUpdateLoading, setTatUpdateLoading] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [feRemarksModalOpen, setFeRemarksModalOpen] = useState(false);
  const [feRemarksValue, setFeRemarksValue] = useState("");
  const [feRemarksUpdateLoading, setFeRemarksUpdateLoading] = useState(false);
  const [importDate, setImportDate] = useState("");
  const [dates, setDates] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [taskIdEditModalOpen, setTaskIdEditModalOpen] = useState(false);
  const [taskIdEditValue, setTaskIdEditValue] = useState("");
  const [taskIdUpdateLoading, setTaskIdUpdateLoading] = useState(false);

  const [feeNameEditModalOpen, setFeeNameEditModalOpen] = useState(false);
  const [feeNameSearchValue, setFeeNameSearchValue] = useState("");
  const [feeNameUpdateLoading, setFeeNameUpdateLoading] = useState(false);
  const [fieldExecutives, setFieldExecutives] = useState([]);

  const [currentTime, setCurrentTime] = useState("");

  const { getFieldExecutivesData, loadingGetFieldExecutives } = useSelector(
    (state) => state.getFieldExecutives || {},
  );

  const [feeNameSearchLoading, setFeeNameSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [taskStatusEditModalOpen, setTaskStatusEditModalOpen] = useState(false);
  const [taskStatusEditValue, setTaskStatusEditValue] = useState("");
  const [taskStatusUpdateLoading, setTaskStatusUpdateLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef(null);
  const [hdfcCardViewOption, setHdfcCardViewOption] = useState(true);
  const [hdfcCardAddOption, setHdfcCardAddOption] = useState(true);
  const [hdfcCardUpdateOption, setHdfcCardUpdateOption] = useState(true);
  const [hdfcCardDeleteOption, setHdfcCardDeleteOption] = useState(true);

  // Export permission logic (like DelhiPage.js)
  let canExport = false;
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    canExport = userInfo && userInfo.role && userInfo.role.usersAdd;
  } catch (e) {
    canExport = false;
  }

  const navigate = useNavigate();

  // Add these useEffect hooks after your existing useEffect declarations

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin);
  const { loadingUserInfo, userInfo } = userLogin;

  useEffect(() => {
    // Check if user info exists
    if (!userInfo) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  // Check for role permissions
  const getRoleInfo = useSelector((state) => state.getRoleInfo);
  const { loadingGetRole, getRoleData } = getRoleInfo;

  useEffect(() => {
    if (getRoleData) {
      // Use driveHdfcCard for HDFC Card page access
      setHdfcCardViewOption(getRoleData.driveHdfcCard);
      setHdfcCardAddOption(getRoleData.driveHdfcCard);
      setHdfcCardUpdateOption(getRoleData.driveHdfcCard);
      setHdfcCardDeleteOption(getRoleData.driveHdfcCard);

      if (!getRoleData.driveHdfcCard) {
        toast("Not Authorized", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        navigate("/");
      }
    }
  }, [userInfo, getRoleData, navigate]);

  const getCurrentISTDisplay = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const currentISTTime = new Date(utc + istOffset);

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };

    return currentISTTime.toLocaleDateString("en-IN", options);
  };

  const fields = [
    "task_id",
    "task_status",
    "date",
    "datetime",
    "reference_no",
    "allo",
    "customer_name",
    "aps_rvr",
    "redo",
    "address",
    "fee_name",
    "zone",
    "file_number",
    "discuss",
    "tally",
    "deo_name",
    "done",
    "tat",
    "date_2",
    "time",
    "fe_remarks",
    "status",
    "reason",
    "remark",
    "f",
    "tat_category",
    "revised_status",
    "supervisor",
    "ocl",
    "pending",
    "zone_code",
    "revised_hub_spoke",
    "revised_tat2",
    "time_slab",
    "before_after_3_30_pm",
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "unassigned", label: "Unassigned" },
    { value: "visit pending", label: "Visit Pending" },
    { value: "finalisation pending", label: "Finalisation Pending" },
    { value: "in progress", label: "In Progress" },
    { value: "mapping", label: "Mapping" },
    { value: "not fired", label: "Not Fired" },
    { value: "waived", label: "Waived" },
    { value: "completed", label: "Completed" },
  ];

  const filterControls = [
    {
      key: "date",
      label: "Date",
      control: (
        <select
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className={driveFilterSelectClass}
        >
          <option value="">Select Date</option>
          {dates.map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      label: "Status",
      control: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const PrimaryButton = ({
    children,
    onClick,
    disabled,
    className,
    loading,
  }) => (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      text={children}
      custom={`bg-blue-500 hover:bg-blue-700 ${className}`}
    />
  );

  const SecondaryButton = ({ children, onClick, className }) => (
    <Button
      onClick={onClick}
      text={children}
      custom={`bg-gray-500 hover:bg-gray-700 ${className}`}
    />
  );

  const DangerButton = ({ children, onClick, className, loading }) => (
    <Button
      onClick={onClick}
      disabled={loading}
      text={children}
      custom={`bg-red-500 hover:bg-red-700 ${className}`}
    />
  );

  const ModalActionButtons = ({ children, className }) => (
    <div className={`flex gap-2 justify-end ${className}`}>{children}</div>
  );

  const columns = [
    { field: "sno", header: "S.No" },
    {
      field: "task_id",
      header: "Task ID",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">
            {rowData.task_id || "-"}
          </span>
          <button
            onClick={() => {
              setTaskIdEditValue(rowData.task_id || "");
              setFormData(rowData);
              setTaskIdEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit Task ID"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "task_status",
      header: "Task Status",
      body: (rowData) => {
        const status = rowData.task_status;

        // Define color mapping (same as other pages)
        const getStatusColor = (status) => {
          if (!status) return "bg-gray-100 text-gray-600"; // Default for empty

          const statusLower = status.toLowerCase().trim();

          switch (statusLower) {
            case "unassigned":
              return "bg-red-100 text-red-800 border-red-200";
            case "visit pending":
              return "bg-orange-100 text-orange-800 border-orange-200";
            case "finalisation pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "completed":
              return "bg-green-100 text-green-800 border-green-200";
            default:
              return "bg-gray-100 text-gray-600 border-gray-200";
          }
        };

        return (
          <div className="flex items-center gap-2 min-w-[160px]">
            <span
              className={`px-3 py-2 rounded-full text-sm font-medium border min-w-[120px] text-center ${getStatusColor(status)}`}
              title={status || "No Status"}
            >
              {status || "-"}
            </span>
            {/* <button
                            onClick={() => {
                                setTaskStatusEditValue(rowData.task_status || '');
                                setFormData(rowData);
                                setTaskStatusEditModalOpen(true);
                            }}
                            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                            title="Edit Task Status"
                        >
                            <Edit className="h-4 w-4" />
                        </button> */}
          </div>
        );
      },
    },
    { field: "import_date", header: "Import Date" },
    { field: "date", header: "Date" },
    { field: "datetime", header: "DateTime" },
    { field: "reference_no", header: "Reference No" },
    { field: "allo", header: "Allo" },
    { field: "customer_name", header: "Customer Name" },
    { field: "aps_rvr", header: "APS RVR" },
    { field: "redo", header: "Redo" },
    {
      field: "address",
      header: "Address",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[100px]">
            {rowData.address?.length > 20
              ? rowData.address.slice(0, 20) + "..."
              : rowData.address || ""}
          </span>
          <button
            onClick={() => {
              setSelectedAddress(rowData.address || "No address available");
              setAddressModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="View Address"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "fee_name",
      header: "Fee Name",
      body: (rowData) => {
        // Hide Fee Name if status is "Sent To Bank"
        if (rowData.task_status === "Sent To Bank") {
          return <div className="text-gray-400">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {rowData.fee_name || "-"}
            </span>
            <button
              onClick={() => {
                setFeeNameSearchValue(rowData.fee_name || "");
                setFormData(rowData);
                setFeeNameEditModalOpen(true);
                // Fetch initial field executives
                dispatch(getFieldExecutives(""));
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit Fee Name"
            >
              <Edit className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    { field: "zone", header: "Zone" },
    { field: "file_number", header: "File Number" },
    { field: "discuss", header: "Discuss" },
    { field: "tally", header: "Tally" },
    { field: "deo_name", header: "DEO Name" },
    { field: "done", header: "Done" },
    // { field: 'type', header: 'Type' },
    {
      field: "tat",
      header: "TAT",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">{rowData.tat || "-"}</span>
          <button
            onClick={() => {
              setTatEditValue(rowData.tat || "");
              setFormData(rowData);
              setTatEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit TAT"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "date_2", header: "Date 2" },
    { field: "time", header: "Time" },
    {
      field: "fe_remarks",
      header: "FE Remarks",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[100px]">
            {rowData.fe_remarks?.length > 20
              ? rowData.fe_remarks.slice(0, 20) + "..."
              : rowData.fe_remarks || ""}
          </span>
          <button
            onClick={() => {
              setFeRemarksValue(rowData.fe_remarks || "");
              setFormData(rowData);
              setFeRemarksModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit FE Remarks"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "status",
      header: "Status",
      body: (rowData) => (
        <span className="text-sm text-gray-700">{rowData.status || "-"}</span>
      ),
    },
    {
      field: "reason",
      header: "Reason",
      body: (rowData) => (
        <span
          className="text-sm text-gray-700 max-w-[150px] truncate block"
          title={rowData.reason || ""}
        >
          {rowData.reason
            ? rowData.reason.length > 30
              ? rowData.reason.slice(0, 30) + "..."
              : rowData.reason
            : "-"}
        </span>
      ),
    },
    {
      field: "remark",
      header: "Remark",
      body: (rowData) => {
        const remark = rowData.remark;

        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {remark
                ? remark.length > 20
                  ? remark.slice(0, 20) + "..."
                  : remark
                : ""}
            </span>
            <button
              onClick={() => {
                setEditRemarkValue(remark || "");
                setFormData(rowData);
                setViewModalOpen(true); // open modal directly in edit mode
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit Remark"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    { field: "f", header: "F" },
    { field: "tat_category", header: "TAT Category" },
    { field: "revised_status", header: "Revised Status" },
    { field: "supervisor", header: "Supervisor" },
    { field: "ocl", header: "OCL" },
    { field: "pending", header: "Pending" },
    { field: "zone_code", header: "Zone Code" },
    { field: "revised_hub_spoke", header: "Revised Hub Spoke" },
    { field: "revised_tat2", header: "Revised TAT2" },
    { field: "time_slab", header: "Time Slab" },
    { field: "before_after_3_30_pm", header: "Before/After 3:30 PM" },
  ];

  useEffect(() => {
    setDates(getLast5IstdatesExcludeToday());
    // Default importDate is today (for main data)
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(utc + istOffset);
    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    setImportDate(`${yyyy}-${mm}-${dd}`);
    setFilterDate("");
  }, []);

  useEffect(() => {
    const validPage = Math.max(1, parseInt(page) || 1);
    if (validPage !== page) {
      setPage(validPage);
      return;
    }
    let query = `search=${search}&page=${validPage}&limit=10`;
    if (filterDate) query += `&importDate=${filterDate}`;
    else query += `&importDate=${importDate}`;
    if (statusFilter) query += `&status=${statusFilter}`;
    dispatch(getHdfcCardRecords(query));
  }, [dispatch, search, page, importDate, filterDate, statusFilter]);

  useEffect(() => {
    console.log("getFieldExecutivesData changed:", getFieldExecutivesData);
    if (getFieldExecutivesData?.data) {
      console.log("Setting fieldExecutives:", getFieldExecutivesData.data);
      setFieldExecutives(getFieldExecutivesData.data);
    }
  }, [getFieldExecutivesData]);

  // Add this callback function
  const handleFeeNameSearch = useCallback(
    async (searchTerm) => {
      console.log("handleFeeNameSearch called with:", searchTerm);
      setFeeNameSearchValue(searchTerm);

      if (searchTerm.length > 0) {
        try {
          setFeeNameSearchLoading(true);
          console.log("Dispatching getFieldExecutives...");

          const result = await dispatch(getFieldExecutives(searchTerm));
          console.log("getFieldExecutives result:", result);

          if (result && result.data) {
            setFieldExecutives(result.data);
            setShowSuggestions(true);
          } else {
            setFieldExecutives([]);
            setShowSuggestions(false);
          }
        } catch (error) {
          console.error("Error searching field executives:", error);
          setFieldExecutives([]);
          setShowSuggestions(false);
        } finally {
          setFeeNameSearchLoading(false);
        }
      } else {
        setFieldExecutives([]);
        setShowSuggestions(false);
      }
    },
    [dispatch],
  );

  const refreshData = async () => {
    try {
      // Sync task statuses, fee names, and remarks first
      console.log("Syncing HDFC Card task data before refresh...");
      const syncResult = await dispatch(syncHdfcCardTaskStatus());

      // If sync returned updated records, update local state first
      if (
        syncResult &&
        syncResult.updatedRecords &&
        syncResult.updatedRecords.length > 0
      ) {
        setData((prevData) => {
          const updatedData = [...prevData];
          syncResult.updatedRecords.forEach((update) => {
            const recordIndex = updatedData.findIndex(
              (record) => record._id === update._id,
            );
            if (recordIndex !== -1) {
              // Update status if it changed
              if (update.new_status) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  task_status: update.new_status,
                };
              }

              // Update Fee Name if it changed
              if (update.new_fee_name) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  fee_name: update.new_fee_name,
                };
              }

              // Update remark if it changed
              if (update.new_remark) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  remark: update.new_remark,
                };
              }
              if (update.new_status_field) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  status: update.new_status_field,
                };
              }

              if (update.new_reason) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  reason: update.new_reason,
                };
              }
            }
          });
          return updatedData;
        });
      }

      // Then fetch updated data
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;

      dispatch(getHdfcCardRecords(query));
    } catch (error) {
      console.error("Error during HDFC Card sync in refreshData:", error);
      // Still fetch data even if sync fails
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;

      dispatch(getHdfcCardRecords(query));
    }
  };

  // Sync on page load/refresh only (automatic interval sync disabled)
  useEffect(() => {
    const performRealtimeSync = async () => {
      try {
        console.log("Starting HDFC Card realtime sync...");

        // Call the sync endpoint
        const syncResult = await dispatch(syncHdfcCardTaskStatus());

        // If there are updated records, update the local state
        if (
          syncResult &&
          syncResult.updatedRecords &&
          syncResult.updatedRecords.length > 0
        ) {
          console.log(
            `Found ${syncResult.updatedRecords.length} updated HDFC Card records`,
          );

          // Update local data state with new statuses, Fee Names, and remarks
          setData((prevData) => {
            const updatedData = [...prevData];
            syncResult.updatedRecords.forEach((update) => {
              const recordIndex = updatedData.findIndex(
                (record) => record._id === update._id,
              );
              if (recordIndex !== -1) {
                // Update status if it changed
                if (update.new_status) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    task_status: update.new_status,
                  };
                }

                // Update Fee Name if it changed
                if (update.new_fee_name) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    fee_name: update.new_fee_name,
                  };
                }

                // Update remark if it changed
                if (update.new_remark) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    remark: update.new_remark,
                  };
                }
              }
            });
            return updatedData;
          });

          // Enhanced toast notification
          const statusUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_status,
          ).length;
          const feeNameUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_fee_name,
          ).length;
          const remarkUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_remark,
          ).length;

          let message = "";
          if (statusUpdates > 0) message += `${statusUpdates} status`;
          if (feeNameUpdates > 0)
            message += `${message ? ", " : ""}${feeNameUpdates} fee name`;
          if (remarkUpdates > 0)
            message += `${message ? ", " : ""}${remarkUpdates} remark`;

          // if (message) {
          //     toast.success(`HDFC Card: ${message} updated automatically`, {
          //         position: "bottom-right",
          //         autoClose: 3000,
          //     });
          // }
        }

        setLastRefresh(new Date());
      } catch (error) {
        console.error("HDFC Card realtime sync failed:", error);
        // Fallback to full refresh if realtime sync fails
        refreshData();
      }
    };

    // Sync on page load/refresh
    performRealtimeSync();
  }, [dispatch]);

  useEffect(() => {
    let interval;
    if (tatEditModalOpen) {
      interval = setInterval(() => {
        setCurrentTime(getCurrentISTDisplay());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tatEditModalOpen]);

  useEffect(() => {
    setData(getHdfcCardRecordsData?.data || []);
  }, [getHdfcCardRecordsData]);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (selectedStatus) => {
    setStatusFilter(selectedStatus);
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      await dispatch(deleteHdfcCardRecord(id));
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      dispatch(getHdfcCardRecords(query));
      toast.success("Record deleted successfully!");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select rows to delete");
      return;
    }
    try {
      const idsArray = selectedRows.map((row) => row._id);
      await dispatch(bulkDeleteHdfcCardRecords(idsArray));
      setSelectedRows([]);
      setShowBulkDeleteConfirm(false);
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      dispatch(getHdfcCardRecords(query));
      toast.success(`Successfully deleted ${idsArray.length} records`);
    } catch (err) {
      console.error("Bulk Delete Error:", err.message);
      toast.error(
        "Bulk Delete Failed: " + (err.response?.data?.error || err.message),
      );
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file to import");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      setLoadingImport(true);
      await dispatch(importHdfcCardRecords(form));
      setModalOpen(false);
      setFile(null);
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      dispatch(getHdfcCardRecords(query));
      toast.success("Import Success!");
    } catch (err) {
      console.error("Import Error:", err.message);
      toast.error(
        "Import Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoadingImport(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      await dispatch(exportHdfcCardRecords());
      toast.success("Export Success!");
    } catch (err) {
      console.error("Export Error:", err.message);
      toast.error(
        "Export Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoadingExport(false);
    }
  };

  const handleEdit = (row) => {
    setFormData(row);
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      setLoadingUpdate(true);
      await dispatch(updateHdfcCardRecord(formData._id, formData));
      setEditModalOpen(false);
      let query = `search=${search}&page=${page}&limit=10`;
      if (filterDate) query += `&importDate=${filterDate}`;
      else query += `&importDate=${importDate}`;
      if (statusFilter) query += `&status=${statusFilter}`;
      dispatch(getHdfcCardRecords(query));
      toast.success("Record updated successfully!");
    } catch (err) {
      console.error("Update Error:", err.message);
      toast.error(
        "Update Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoadingUpdate(false);
    }
  };

  const renderCell = (row, field) => {
    if ((field === "date" || field === "date_2") && row[field]) {
      const d = new Date(row[field]);
      return (
        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {d.toLocaleDateString("en-GB")}
        </div>
      );
    }

    if (field === "datetime" && row[field]) {
      const d = new Date(row[field]);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).toString().slice(-2);
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return (
        <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {`${day}-${month}-${year} ${hours}:${minutes}`}
        </div>
      );
    }

    return (
      <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {row[field] || "-"}
      </div>
    );
  };

  const actions = [
    {
      icon: <Edit className="h-4 w-4" />,
      className:
        "text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50",
      onClick: (row) => handleEdit(row),
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      className: "text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50",
      onClick: (row) => handleDelete(row._id),
    },
  ];

  // Add formatted import_date to each row
  const formattedData = (data || []).map((row) => ({
    ...row,
    import_date: row.createdAt
      ? (() => {
          const dateObj = new Date(row.createdAt);
          if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, "0");
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const year = String(dateObj.getFullYear()).slice(-2);
            return `${day}-${month}-${year}`;
          }
          return "-";
        })()
      : "-",
  }));

  // Helper to get last 3 IST dates in YYYY-MM-DD, excluding today
  function getLast5IstdatesExcludeToday() {
    const dates = [];
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    // Include today (i = 0) and go back 6 more days for total of 7 days
    for (let i = 0; i <= 6; i++) {
      const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
      const yyyy = istTime.getFullYear();
      const mm = String(istTime.getMonth() + 1).padStart(2, "0");
      const dd = String(istTime.getDate()).padStart(2, "0");
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Remark copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy remark");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  HDFC Card Records
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and view HDFC Card records data • Click any cell to
                  edit inline
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedRows.length > 0 && (
                  <DangerButton
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="flex items-center gap-2"
                    disabled={bulkDeleteLoading}
                  >
                    <Trash className="h-4 w-4" />
                    Delete Selected ({selectedRows.length})
                  </DangerButton>
                )}
                <PrimaryButton
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>

          <DriveFilters
            searchValue={search}
            onSearchChange={handleSearch}
            searchPlaceholder="Search by customer name,reference no,zone,fee name,deo name..."
            filters={filterControls}
            total={total}
            columnsClass="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          />
          {selectedRows.length > 0 && (
            <div className="px-6 pb-4">
              <div className="inline-flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-700">
                ✓ {selectedRows.length} record
                {selectedRows.length !== 1 ? "s" : ""} selected
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-hidden">
            {loading ? (
              <div className="py-12">
                <Loading />
              </div>
            ) : (
              <div className="overflow-x-auto" ref={tableRef}>
                <Table
                  data={formattedData}
                  columns={columns}
                  loading={loading}
                  pagination={{ totalRecords: total }}
                  currentPage={page}
                  onPageChange={setPage}
                  actions={actions}
                  selectable={true}
                  selectedRows={selectedRows}
                  onSelectionChange={setSelectedRows}
                  renderCell={renderCell}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                Delete Selected Records
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete {selectedRows.length} selected
                records? This action cannot be undone.
              </p>

              <ModalActionButtons>
                <DangerButton
                  onClick={handleBulkDelete}
                  loading={bulkDeleteLoading}
                  className="flex items-center gap-2"
                >
                  <Trash className="h-4 w-4" />
                  Delete {selectedRows.length} Records
                </DangerButton>
                <SecondaryButton
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Import/Export Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Import / Export Data
              </h3>
              {/* Template Download Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  📋 Excel Template Format
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <h5 className="text-sm font-semibold text-yellow-800 mb-2">
                        ⚠️ Important Notes:
                      </h5>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Column names are case-sensitive</li>
                        <li>• Date format: DD/MM/YYYY</li>
                        <li>• Time format: HH:MM AM/PM</li>
                        <li>• TAT should be numeric values</li>
                        <li>• Empty cells will be treated as empty strings</li>
                        <li>• '#N/A' values will be preserved as-is</li>
                        <li>• Zone field defaults to '#N/A' if empty</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-green-800 mb-2">
                        ✅ Download Template:
                      </h5>
                      <p className="text-xs text-green-700 mb-3">
                        Click below to download a sample Excel template with the
                        correct column structure.
                      </p>
                      <button
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = "/hdfc_card_template.xlsx";
                          link.download = "HDFC_Card_Import_Template.xlsx";
                          link.click();
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        📥 Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* File Upload Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select file to import
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                </div>
              </div>
              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleImport}
                  disabled={!file}
                  loading={loadingImport}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    loading={loadingExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
                <SecondaryButton onClick={() => setModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Record
              </h3>

              <div className="max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields.map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                        {field.replace(/_/g, " ")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData[field] || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [field]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <ModalActionButtons className="mt-6">
                <PrimaryButton
                  onClick={handleUpdate}
                  loading={loadingUpdate}
                  className="flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Update
                </PrimaryButton>
                <SecondaryButton onClick={() => setEditModalOpen(false)}>
                  Cancel
                </SecondaryButton>
              </ModalActionButtons>
            </div>
          </div>
        </div>
      )}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Edit Remark
                </h3>

                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={() => copyToClipboard(editRemarkValue)}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </PrimaryButton>

                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setRemarkUpdateLoading(true);
                        const updated = {
                          ...formData,
                          remark: editRemarkValue,
                        };
                        await dispatch(
                          updateHdfcCardRecord(`${formData._id}`, updated),
                        );
                        setViewModalOpen(false);
                        let query = `search=${search}&page=${page}&limit=10`;
                        if (filterDate) query += `&importDate=${filterDate}`;
                        else query += `&importDate=${importDate}`;
                        if (statusFilter) query += `&status=${statusFilter}`;
                        dispatch(getHdfcCardRecords(query));
                        toast.success("Remark updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update remark");
                      } finally {
                        setRemarkUpdateLoading(false);
                      }
                    }}
                    loading={remarkUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>

                  <button
                    onClick={() => setViewModalOpen(false)}
                    disabled={remarkUpdateLoading}
                    className="p-2 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    title="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "400px" }}
              >
                <textarea
                  value={editRemarkValue}
                  onChange={(e) => setEditRemarkValue(e.target.value)}
                  className="w-full h-full p-0 border-none bg-transparent text-sm text-gray-700 whitespace-pre-wrap leading-relaxed resize-none focus:outline-none overflow-hidden"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {tatEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit TAT
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTatUpdateLoading(true);

                        // Get current IST time when TAT is filled
                        const now = new Date();
                        const utc =
                          now.getTime() + now.getTimezoneOffset() * 60000;
                        const istOffset = 5.5 * 60 * 60 * 1000;
                        const currentISTTime = new Date(utc + istOffset);

                        // Format punch_date (YYYY-MM-DD for database)
                        const yyyy = currentISTTime.getFullYear();
                        const mm = String(
                          currentISTTime.getMonth() + 1,
                        ).padStart(2, "0");
                        const dd = String(currentISTTime.getDate()).padStart(
                          2,
                          "0",
                        );
                        const formattedPunchDate = `${yyyy}-${mm}-${dd}`;

                        // Format punch_time (HH:MM AM/PM)
                        let hours = currentISTTime.getHours();
                        const minutes = currentISTTime.getMinutes();
                        const ampm = hours >= 12 ? "PM" : "AM";
                        hours = hours % 12 || 12;
                        const formattedPunchTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

                        const updated = {
                          ...formData,
                          tat: tatEditValue,
                          date_2: formattedPunchDate, // Note: Using date_2 based on your columns
                          time: formattedPunchTime, // Note: Using time based on your columns
                          // Automatically set task_status to "Sent To Bank" when TAT is filled
                          task_status:
                            tatEditValue && tatEditValue.trim() !== ""
                              ? "Sent To Bank"
                              : formData.task_status,
                          manualTaskStatus: true, // Mark as manually modified
                        };

                        await dispatch(
                          updateHdfcCardRecord(`${formData._id}`, updated),
                        );
                        setTatEditModalOpen(false);
                        let query = `search=${search}&page=${page}&limit=10`;
                        if (filterDate) query += `&importDate=${filterDate}`;
                        else query += `&importDate=${importDate}`;
                        if (statusFilter) query += `&status=${statusFilter}`;
                        dispatch(getHdfcCardRecords(query));
                        toast.success(
                          'TAT updated with current punch time and status set to "Sent To Bank"!',
                        );
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update TAT");
                      } finally {
                        setTatUpdateLoading(false);
                      }
                    }}
                    loading={tatUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTatEditModalOpen(false)}
                    disabled={tatUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  value={tatEditValue}
                  onChange={(e) => setTatEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter TAT "
                />
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-sm">
                    <span className="font-medium text-blue-800">
                      Current IST Time:{" "}
                    </span>
                    <span className="text-blue-700">
                      {getCurrentISTDisplay()}
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    This time will be captured as punch time when you save
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {addressModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  View Address
                </h3>
                <SecondaryButton onClick={() => setAddressModalOpen(false)}>
                  Close
                </SecondaryButton>
              </div>

              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "300px" }}
              >
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedAddress}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {feRemarksModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Edit FE Remarks
                </h3>

                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setFeRemarksUpdateLoading(true);
                        const updated = {
                          ...formData,
                          fe_remarks: feRemarksValue,
                        };
                        await dispatch(
                          updateHdfcCardRecord(`${formData._id}`, updated),
                        );
                        setFeRemarksModalOpen(false);
                        let query = `search=${search}&page=${page}&limit=10`;
                        if (filterDate) query += `&importDate=${filterDate}`;
                        else query += `&importDate=${importDate}`;
                        if (statusFilter) query += `&status=${statusFilter}`;
                        dispatch(getHdfcCardRecords(query));
                        toast.success("FE Remarks updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update FE Remarks");
                      } finally {
                        setFeRemarksUpdateLoading(false);
                      }
                    }}
                    loading={feRemarksUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>

                  <SecondaryButton
                    onClick={() => setFeRemarksModalOpen(false)}
                    disabled={feRemarksUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>

              <div
                className="bg-gray-50 p-4 rounded-md border"
                style={{ height: "300px" }}
              >
                <textarea
                  value={feRemarksValue}
                  onChange={(e) => setFeRemarksValue(e.target.value)}
                  className="w-full h-full p-0 border-none bg-transparent text-sm text-gray-700 whitespace-pre-wrap leading-relaxed relength-none focus:outline-none overflow-hidden"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Task ID Edit Modal */}
      {taskIdEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task ID
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskIdUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_id: taskIdEditValue,
                        };
                        await dispatch(
                          updateHdfcCardRecord(formData._id, updated),
                        );
                        setTaskIdEditModalOpen(false);
                        dispatch(
                          getHdfcCardRecords(
                            `search=${search}&page=${page}&limit=10&importDate=${filterDate || importDate}`,
                          ),
                        );
                        toast.success("Task ID updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update Task ID");
                      } finally {
                        setTaskIdUpdateLoading(false);
                      }
                    }}
                    loading={taskIdUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskIdEditModalOpen(false)}
                    disabled={taskIdUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="number"
                  value={taskIdEditValue}
                  onChange={(e) => setTaskIdEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task ID"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Status Edit Modal */}
      {taskStatusEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Task Status
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setTaskStatusUpdateLoading(true);
                        const updated = {
                          ...formData,
                          task_status: taskStatusEditValue,
                        };
                        await dispatch(
                          updateHdfcCardRecord(formData._id, updated),
                        );
                        setTaskStatusEditModalOpen(false);
                        dispatch(
                          getHdfcCardRecords(
                            `search=${search}&page=${page}&limit=10&importDate=${filterDate || importDate}`,
                          ),
                        );
                        toast.success("Task Status updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update Task Status");
                      } finally {
                        setTaskStatusUpdateLoading(false);
                      }
                    }}
                    loading={taskStatusUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => setTaskStatusEditModalOpen(false)}
                    disabled={taskStatusUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border">
                <input
                  type="text"
                  value={taskStatusEditValue}
                  onChange={(e) => setTaskStatusEditValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Task Status"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Fee Name Edit Modal */}
      {feeNameEditModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Fee Name
                </h3>
                <div className="flex items-center gap-2">
                  <PrimaryButton
                    onClick={async () => {
                      try {
                        setFeeNameUpdateLoading(true);
                        const updated = {
                          ...formData,
                          fee_name: feeNameSearchValue,
                        };
                        await dispatch(
                          updateHdfcCardRecord(formData._id, updated),
                        );
                        setFeeNameEditModalOpen(false);
                        setShowSuggestions(false);
                        refreshData();
                        toast.success("Fee Name updated successfully!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update Fee Name");
                      } finally {
                        setFeeNameUpdateLoading(false);
                      }
                    }}
                    loading={feeNameUpdateLoading}
                    className="flex items-center gap-2"
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setFeeNameEditModalOpen(false);
                      setShowSuggestions(false);
                      setFieldExecutives([]);
                    }}
                    disabled={feeNameUpdateLoading}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md border relative">
                <div className="relative">
                  <input
                    type="text"
                    value={feeNameSearchValue}
                    onChange={(e) => handleFeeNameSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search Field Executive..."
                    autoComplete="off"
                  />

                  {feeNameSearchLoading && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  {showSuggestions && fieldExecutives.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {fieldExecutives.map((executive) => (
                        <div
                          key={executive._id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setFeeNameSearchValue(executive.name);
                            setShowSuggestions(false);
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {executive.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {executive.email} • {executive.identifier}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showSuggestions &&
                    fieldExecutives.length === 0 &&
                    feeNameSearchValue.length > 0 &&
                    !feeNameSearchLoading && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No Field Executives found
                        </div>
                      </div>
                    )}
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Type to search for Field Executive users
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HdfcCardPage;
